import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Notification bell for the top bar.
 * - Polls /api/notifications every 60s when logged in
 * - Shows unread count badge
 * - Click opens a dropdown with recent items + "mark all as read"
 */
export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const popRef = useRef(null);

  const load = async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications');
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    load();
    if (!user) return;
    const iv = setInterval(load, 60_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (popRef.current && !popRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const markRead = async (nid) => {
    try { await api.post(`/notifications/${nid}/read`); setItems(prev => prev.map(x => x.id === nid ? { ...x, read: true } : x)); setUnread(u => Math.max(0, u - 1)); } catch {}
  };
  const markAll = async () => {
    try { await api.post('/notifications/mark-all-read'); setItems(prev => prev.map(x => ({ ...x, read: true }))); setUnread(0); } catch {}
  };

  if (!user) return null;

  return (
    <div className="relative" ref={popRef}>
      <button
        data-testid="notif-bell"
        onClick={() => { setOpen(o => !o); if (!open) load(); }}
        className="relative w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4 text-slate-200" />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-dropdown" className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-20px)] glass rounded-2xl border border-white/10 shadow-2xl overflow-hidden z-50">
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <div className="font-display font-bold text-sm">Notifications</div>
            {unread > 0 && (
              <button data-testid="notif-mark-all" onClick={markAll} className="text-[11px] text-violet-400 hover:underline">Mark all read</button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && (
              <div className="p-6 text-center text-slate-500 text-sm">No notifications yet</div>
            )}
            {items.map(n => {
              const inner = (
                <div className={`flex items-start gap-2 p-3 border-b border-white/5 last:border-0 ${n.read ? 'opacity-60' : 'bg-violet-500/5'}`}>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-violet-400 mt-1.5 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{n.title}</div>
                    {n.body && <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{n.body}</div>}
                    <div className="text-[10px] text-slate-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.read && (
                    <button data-testid={`notif-read-${n.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); markRead(n.id); }} className="text-slate-500 hover:text-white" aria-label="Mark read">
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
              return n.cta_url
                ? <Link key={n.id} to={n.cta_url} onClick={() => { setOpen(false); if (!n.read) markRead(n.id); }}>{inner}</Link>
                : <div key={n.id} onClick={() => !n.read && markRead(n.id)} className="cursor-pointer">{inner}</div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
